import React, { ReactElement } from 'react'
// Externals imports.
import { TextField, TextFieldProps } from '@material-ui/core'

import Label from '../Label'
import Icon from '../Icon'
import { IconName, IconSize } from '../../../../NonReact/constants/React/Icon/Constants'
// CSS imports. Commented out because of current build process.
// import './textInput.scss'
import { ReactRef } from '../../Types/Types'

/**
 * Helper text properties type.
 */
type HelperTextProps = {
    helperText?: string
    error?: boolean
}

/**
 * Component properties type.
 */
type Props = {
    labelInputClassName?: string
    classNames?: string
    name?: string
    inputRef?: ReactRef
    defaultValue?: string
    error?: string
    helpText?: string
    placeholder?: string
    labelInput?: string
    labelLeft?: string
    disabled?: boolean
    isLabelHidden?: boolean
    elementRef?: React.MutableRefObject<null>
    onClick?: () => void
    customDropdown?: boolean
    isOpen?: boolean
    value?: string
    onChange?: (sNewValue: string) => void
    readOnly?: boolean
    variant?: 'standard' | 'outlined' | 'filled'
}

/**
 * Text input component.
 *
 * @param name Name of the field - Used for hook forms.
 * @param inputRef React reference - Used for hook forms.
 * @param defaultValue Default value set for text input.
 * @param error Error message for this input.
 * @param helpText Help text for input.
 * @param onChange Change handler.
 * @param placeholder Placeholder text.
 * @param labelInput Label shown in the input.
 * @param labelLeft Label shown on the left side of the input.
 * @param disabled
 * @param isLabelHidden
 * @param elementRef
 * @param onClick
 * @param customDropdown
 * @param isOpen
 * @param readOnly
 */
const TextInputDropDown: React.FC<Props> = ({
    labelInputClassName,
    classNames,
    name,
    inputRef,
    value,
    onChange,
    defaultValue,
    error,
    helpText,
    placeholder,
    labelInput,
    labelLeft,
    disabled,
    isLabelHidden,
    elementRef,
    onClick,
    customDropdown,
    isOpen,
    readOnly,
    variant
}: Props): ReactElement => {
    /**
     * Prepares helper text properties.
     */
    const getHelperTextProps = (): HelperTextProps => {
        const helperTextProps: HelperTextProps = {}

        if (error) {
            helperTextProps.helperText = error
            helperTextProps.error = true
        } else if (helpText) {
            helperTextProps.helperText = helpText
        }

        return helperTextProps
    }

    const labelClass: string = `label-left ${labelInputClassName} ${isLabelHidden ? 'visually-hidden' : ''}`

    const textFieldProps: TextFieldProps = {
        name,
        inputRef,
        value,
        onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
            if (onChange) {
                onChange(e?.currentTarget?.value ?? '')
            }
        },
        InputProps: {
            readOnly
        }
    }

    return (
        <div className={`text-input text-input-variant-${variant ?? 'standard'}`}>
            {labelLeft && <Label customClass={labelClass}>{labelLeft}</Label>}
            <div className={`input ${labelInputClassName}`}>
                <TextField
                    className={classNames}
                    {...textFieldProps}
                    label={labelInput}
                    defaultValue={defaultValue}
                    helperText={error}
                    placeholder={placeholder || undefined}
                    disabled={disabled}
                    {...getHelperTextProps()}
                    ref={elementRef}
                    onClick={onClick}
                    variant={variant}
                />
                {customDropdown && (
                    <div className='MuiSelect-icon'>
                        <Icon
                            iconName={isOpen ? IconName.ARROW_DROPUP : IconName.ARROW_DROPDOWN}
                            iconSize={IconSize.LARGE}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default TextInputDropDown
